import React, { useEffect, useMemo, useState } from 'react';

import { Moment } from 'moment';

import type { WebhookEventSubscription } from '../../typescript-types/webhook-event-subscriptions/webhook-event-subscription';

import { DatePickerError, UiDatePicker } from '../../ui/ui-date-picker';
import { UiTextField } from '../../ui/ui-text-field';

import { EMPLOYED_STATUS, VALIDATING_STATUS } from '../../config/application-status-config';
import { isTruthy } from '../../util/is-truthy';

type InputProps = {
  toStatus: ApplicationStatus;
  inovaWebhookSubscription: WebhookEventSubscription;
  email: string | null;
  setEmail: (email: string | null) => void;
  startDate: Moment | null;
  setStartDate: (startDate: Moment | null) => void;
  setSaveDisabled: (isDisabled: boolean) => void;
};

const ALL_FIELDS = { EMAIL: 'email', START_DATE: 'startDate' } as const;

export const InovaStatusChangeForm = (props: InputProps) => {
  const {
    toStatus,
    inovaWebhookSubscription,
    email,
    setEmail,
    startDate,
    setStartDate,
    setSaveDisabled,
  } = props;

  const REQUIRED_FIELDS: Array<string> = useMemo(
    () =>
      inovaWebhookSubscription.subscriptionDetail.createRecordType === 'applicant'
        ? [ALL_FIELDS.EMAIL]
        : [ALL_FIELDS.START_DATE],
    [inovaWebhookSubscription.subscriptionDetail.createRecordType],
  );

  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // When the `fieldValues` or `errors` change, check if the save button should be disabled.
  // It should be disabled if:
  // There are any required fields with empty values
  // or there are any errors
  useEffect(() => {
    const hasAllRequiredFields =
      (!REQUIRED_FIELDS.includes(ALL_FIELDS.EMAIL) || email) &&
      (!REQUIRED_FIELDS.includes(ALL_FIELDS.START_DATE) || startDate);
    const hasErrors =
      !!Object.keys(errors).length && !!Object.values(errors).filter(isTruthy).length;

    setSaveDisabled(!hasAllRequiredFields || hasErrors);
  }, [REQUIRED_FIELDS, email, errors, setSaveDisabled, startDate]);

  const showStartDateField =
    REQUIRED_FIELDS.includes(ALL_FIELDS.START_DATE) ||
    [VALIDATING_STATUS, EMPLOYED_STATUS].includes(toStatus);

  const fieldOnError = (fieldName: string) => (newError: DatePickerError | string | null) => {
    setErrors({ ...errors, [fieldName]: newError });
  };

  return (
    <div className="u-margin-top-md">
      <UiTextField
        label="Email"
        className="u-display-block u-width-100 u-margin-bottom"
        type="email"
        value={email}
        onChange={setEmail}
        onError={fieldOnError(ALL_FIELDS.EMAIL)}
        errorText={errors[ALL_FIELDS.EMAIL] || ''}
        required={REQUIRED_FIELDS.includes(ALL_FIELDS.EMAIL)}
      />
      {showStartDateField ? (
        <UiDatePicker
          label="Start Date"
          className="u-display-block u-margin-bottom"
          defaultDate={startDate}
          onChange={setStartDate}
          required={REQUIRED_FIELDS.includes(ALL_FIELDS.START_DATE)}
          onError={fieldOnError(ALL_FIELDS.START_DATE)}
          autoOk
        />
      ) : null}
    </div>
  );
};
